import { config } from '@iheartjane/config';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useRuntimeConfig } from '@iheartjane/runtime-config';
import { trackError } from '@iheartjane/tracking';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import type { JaneAuthenticationErrors } from '@jane/shared-ecomm/components';
import { trackCustomerRegistration } from '@jane/shared-ecomm/tracking';
import { setJwt } from '@jane/shared/auth';
import { userQueryKeys } from '@jane/shared/data-access';
import type { AppleAuthError, AppleAuthResponse } from '@jane/shared/hooks';
import { useAppleSDK } from '@jane/shared/hooks';
import { useEcommApp } from '@jane/shared/providers';

import { useCustomerDispatch } from '../../customer/dispatch';
import { openModal } from '../../customer/redux/application';
import {
  authenticateAppleCode,
  authenticateGoogleCode,
  logInError,
  logInSuccess,
  registerSuccess,
} from '../../customer/redux/customer';
import { selectEmbeddedApp } from '../../customer/redux/embeddedApp';
import { setLoginValue } from '../../customer/redux/login';
import { showSuccessNotification } from '../../customer/redux/notifications';
import { useCustomerSelector } from '../../customer/selectors';
import { validationsToErrors } from '../../lib/form';
import { CustomerSource } from '../../sources/customer';
import type { RegisterSuccessResponse } from '../../sources/registration';
import { RegistrationSource } from '../../sources/registration';
import { ResetPasswordRequestSource } from '../../sources/resetPasswordRequest';

interface RegistrationValues {
  email: string;
  nickname?: string;
  password: string;
  phone: string;
  promotionsOptIn?: boolean;
}

interface UseAuthProps {
  initialStoreId?: number;
  isModal?: boolean;
  onLoginSuccess?: () => void;
}

export const useAuth = ({
  onLoginSuccess,
  isModal = true,
  initialStoreId,
}: UseAuthProps = {}) => {
  const { appMode } = useEcommApp();
  const { state } = useLocation() as { state: { nextPathname?: string } };
  const navigate = useNavigate();
  const runtimeConfig = useRuntimeConfig();
  const queryClient = useQueryClient();
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [errors, setErrors] = useState<JaneAuthenticationErrors | null>(null);
  const appleLoginEnabled = useFlag(FLAGS.c3BoostAppleOauth);

  const nextPathname = isModal ? null : state?.nextPathname || '/';

  const {
    isLoaded: appleLoaded,
    initAuth: loadAppleScript,
    signIn,
  } = useAppleSDK();

  const { partnerId, partnerName } = useCustomerSelector(selectEmbeddedApp);

  // google SSO may be implemented for boost
  // apple has not bee implemented yet
  const showGoogleLogin =
    !runtimeConfig.isFramelessEmbedMode ||
    !!runtimeConfig.ssoCredentials['google_oauth2'];
  const showAppleLogin = appleLoginEnabled
    ? true
    : !runtimeConfig.isFramelessEmbedMode;

  useEffect(() => {
    if (!googleLoaded) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      document.body.appendChild(script);
      setGoogleLoaded(true);
    }

    // NOTE: The additional useCallback wrapper broke Verilife checkout on prod (see useAppleSDK)
    // since I can't repro it on staging, we'll wrap it all in a FF
    if (!appleLoaded && !appleLoginEnabled) {
      loadAppleScript();
    }
  }, [googleLoaded, appleLoaded, appleLoginEnabled]);

  useEffect(() => {
    if (appleLoginEnabled) {
      const cleanup = loadAppleScript();
      return () => cleanup();
    }
    return undefined;
  }, [loadAppleScript]);

  const dispatch = useCustomerDispatch();

  const handleResetError = () => {
    setErrors(null);
  };

  const handleLoginSuccess = () => {
    queryClient.invalidateQueries({ queryKey: userQueryKeys.janeUser() });
    onLoginSuccess && onLoginSuccess();
  };

  const handleDefaultLogin = async (
    credentials: {
      email: string;
      password: string;
    },
    source: string
  ) => {
    handleResetError();
    return CustomerSource.logIn(credentials).then((response) => {
      const { body, headers } = response;
      const { user, errors } = body;

      dispatch(setLoginValue({ name: 'email', value: credentials.email }));
      if (errors) {
        setErrors({ login: errors.error });

        return dispatch(logInError(errors.error));
      }

      dispatch(
        logInSuccess({
          user,
          headers,
          nextPathname,
          navigate: isModal ? false : navigate,
          source,
        })
      );
      handleLoginSuccess();
      return undefined;
    });
  };

  const handleLogOut = () => {
    return dispatch(openModal({ name: 'logout' }));
  };

  const handleRegisterSuccess = useCallback(
    (response: RegisterSuccessResponse) => {
      const { headers, body } = response;

      setJwt(headers['Authorization']);
      trackCustomerRegistration({
        customer: body.user,
        partnerChannel: appMode,
        partnerId,
        partnerName,
      });
      dispatch(registerSuccess(body.user));
      handleLoginSuccess();
    },
    [appMode, dispatch, partnerId, partnerName]
  );

  const handleRegister = async (newUser: RegistrationValues) => {
    handleResetError();

    return RegistrationSource.register({
      app_mode_at_registration: appMode,
      store_id_at_registration: initialStoreId,
      ...newUser,
    }).then((response) => {
      const { body } = response;

      if ('errors' in body) {
        dispatch(setLoginValue({ name: 'email', value: newUser.email }));

        setErrors({
          register: Object.values(validationsToErrors(body.errors.validations)),
        });
      }

      return handleRegisterSuccess(response as RegisterSuccessResponse);
    });
  };

  const handleGoogleLogin = (code: unknown, source: string) => {
    dispatch(
      authenticateGoogleCode({
        code,
        onAuthSuccess: handleLoginSuccess,
        nextPathname,
        navigate: isModal ? false : navigate,
        source,
      })
    );
  };

  const handleGoogleClick = (source: string) => {
    const ssoCredentials = runtimeConfig.ssoCredentials;

    const googleClientId = ssoCredentials['google_oauth2'];
    const clientId = googleClientId || config.googleClientId;
    const embedConfigId = runtimeConfig.embedConfigId;

    window.google.accounts.oauth2
      .initCodeClient({
        client_id: clientId,
        scope: 'profile email',
        callback: (response) => {
          try {
            handleGoogleLogin(response, source);
          } catch (error) {
            setErrors({ login: 'Google login failed' });
            trackError(new Error('Google login failed'), {
              googleError: error,
            });
          }
        },
        state: embedConfigId ? `embedConfigId+${embedConfigId}` : undefined,
      })
      .requestCode();
  };

  const handleAppleLogin = (
    authMetadata: AppleAuthResponse,
    source: string
  ) => {
    dispatch(
      authenticateAppleCode({
        authMetadata,
        onAuthSuccess: handleLoginSuccess,
        nextPathname,
        navigate: isModal ? false : navigate,
        source,
      })
    );
  };

  const handleAppleClick = async (source: string) => {
    const data = await signIn();

    if ((data as AppleAuthResponse).authorization) {
      handleAppleLogin(data as AppleAuthResponse, source);
    } else if ((data as AppleAuthError).shouldLogError) {
      setErrors({ login: 'Apple login failed' });
      trackError(new Error('Apple login failed'), {
        appleError: (data as AppleAuthError).error,
      });
    }
  };

  const handleResetPassword = async ({ email }: { email: string }) => {
    handleResetError();
    return ResetPasswordRequestSource.create(email).then((response) => {
      const { success, errors } = response;

      if (errors && errors.error) {
        setErrors({ resetPassword: errors.error });
        return false;
      } else {
        dispatch(showSuccessNotification(success && success.message));
        return true;
      }
    });
  };

  return {
    appleLoaded,
    defaultLogin: handleDefaultLogin,
    errors,
    googleLoaded,
    logOut: handleLogOut,
    onAppleClick: handleAppleClick,
    onGoogleClick: handleGoogleClick,
    register: handleRegister,
    resetError: handleResetError,
    resetPassword: handleResetPassword,
    showAppleLogin,
    showGoogleLogin,
  };
};
